<template>
  <div class="page">
    <!-- 统一风格页头 -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">分校學生編號表</h1>
        <p class="page-subtitle">管理分校學生編號信息，支援導入、新增和編輯操作</p>
      </div>
      <div class="header-right">
        <div class="header-actions">
          <importExcel @importExcel="importExcel" class="import-btn"></importExcel>
          <el-button type="warning" @click="onAdd" icon="el-icon-plus">
            新增編號
          </el-button>
        </div>
      </div>
    </div>

    <div class="layout">
      <!-- 筛选搜索区 -->
      <div class="search-card">
        <!-- PC端筛选栏（≥768px显示） -->
        <div class="pc-search">
          <div class="search-row">
            <div class="search-item">
              <div class="search-text">分校學生編號</div>
              <el-input
                  v-model="formLabelAlign.numberLike"
                  placeholder="搜尋分校學生編號"
                  size="small"
                  style="width: 100%;"
                  clearable
              ></el-input>
            </div>
            <div class="search-item">
              <div class="search-text">電話號碼</div>
              <el-input
                  v-model="formLabelAlign.phoneLike"
                  placeholder="搜尋電話號碼"
                  size="small"
                  style="width: 100%;"
                  clearable
              ></el-input>
            </div>
            <div class="search-item search-button">
              <el-button size="small" type="warning" @click="onSearch" icon="el-icon-search">
                搜尋
              </el-button>
            </div>
            <div class="search-item reset-button">
              <el-button size="small" type="default" @click="onReset" icon="el-icon-refresh">
                清除篩選
              </el-button>
            </div>
          </div>
        </div>

        <!-- 移动端筛选栏（<768px显示） -->
        <div class="mobile-search">
          <el-collapse>
            <el-collapse-item title="篩選條件" name="1">
              <div class="mobile-search-form">
                <el-form size="small" label-width="100px">
                  <el-form-item label="分校學生編號">
                    <el-input
                        v-model="formLabelAlign.numberLike"
                        placeholder="搜尋分校學生編號"
                        clearable
                    ></el-input>
                  </el-form-item>
                  <el-form-item label="電話號碼">
                    <el-input
                        v-model="formLabelAlign.phoneLike"
                        placeholder="搜尋電話號碼"
                        clearable
                    ></el-input>
                  </el-form-item>
                  <el-form-item>
                    <div class="mobile-search-buttons">
                      <el-button type="warning" @click="onSearch" icon="el-icon-search" style="width: 48%;">
                        搜尋
                      </el-button>
                      <el-button @click="onReset" icon="el-icon-refresh" style="width: 48%;">
                        重置
                      </el-button>
                    </div>
                  </el-form-item>
                </el-form>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- 数据展示区 -->
      <div class="data-container">
        <!-- PC端表格（≥768px显示） -->
        <div class="pc-table">
          <el-table
              @sort-change="handleSortChange"
              :data="tableData"
              style="width: 100%"
              border
              stripe
              size="small"
              :empty-text="tableData.length === 0 ? '暫無分校學生編號數據' : ''"
          >
            <el-table-column label="#" type="index" width="60"> </el-table-column>
            <el-table-column
                sortable="custom"
                prop="number"
                label="分校學生編號"
                align="center"
                min-width="120"
            >
              <template slot-scope="{ row }">
                <span class="student-number">{{ row.number }}</span>
              </template>
            </el-table-column>
            <el-table-column
                prop="phone"
                label="電話號碼"
                align="center"
                sortable="custom"
                min-width="130"
            >
              <template slot-scope="{ row }">
                <span>{{ row.phone || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column
                prop="chineseName"
                label="學生名字"
                align="center"
                sortable="custom"
                min-width="150"
            >
              <template slot-scope="{ row }">
                <div class="name-wrapper">
                  <div class="english-name">{{ row.engName }} {{ row.given }} {{ row.surname }} {{ row.chineseName}}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column
                prop="teacherName"
                label="老師名字"
                align="center"
                sortable="custom"
                min-width="120"
            >
              <template slot-scope="{ row }">
                <span>{{ row.teacherName || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column
                prop="status"
                label="狀態"
                align="center"
                min-width="100"
            >
              <template slot-scope="{ row }">
                <el-tag
                    :type="row.status === 1 ? 'success' : 'danger'"
                    size="mini"
                >
                  {{ row.status | studentCodeStatus }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" align="center">
              <template slot-scope="{ row }">
                <el-button
                    type="text"
                    size="small"
                    @click="onEdit(row, row.id)"
                    icon="el-icon-edit"
                    class="edit-btn"
                >
                  編輯
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片列表（<768px显示） -->
        <div class="mobile-cards">
          <div class="student-card" v-for="(row, index) in tableData" :key="row.id || index">
            <!-- 卡片头部：编号 + 状态 -->
            <div class="card-header">
              <div class="student-number">
                <span class="label">編號：</span>
                <span class="value">{{ row.number }}</span>
              </div>
              <el-tag
                  :type="row.status === 1 ? 'success' : 'danger'"
                  size="mini"
                  class="status-tag"
              >
                {{ row.status | studentCodeStatus }}
              </el-tag>
            </div>

            <!-- 卡片主体 -->
            <div class="card-body">
              <div class="info-item">
                <span class="label">電話：</span>
                <span class="value">{{ row.phone || '未填寫' }}</span>
              </div>
              <div class="info-item">
                <span class="label">學生姓名：</span>
                <div class="value name-value">
                  <div></div>
                  <div class="english-name">{{ row.engName }} {{ row.given }} {{ row.surname }} {{ row.chineseName}}</div>
                </div>
              </div>
              <div class="info-item">
                <span class="label">負責老師：</span>
                <span class="value">{{ row.teacherName || '未填寫' }}</span>
              </div>
            </div>

            <!-- 卡片操作栏 -->
            <div class="card-actions">
              <el-button
                  type="warning"
                  size="mini"
                  @click="onEdit(row, row.id)"
                  icon="el-icon-edit"
                  style="width: 100%;"
              >
                編輯信息
              </el-button>
            </div>
          </div>

          <!-- 空数据提示 -->
          <div class="empty-tip" v-if="tableData.length === 0">
            <i class="el-icon-user"></i>
            <p>暫無分校學生編號數據</p>
            <el-button type="warning" size="small" @click="onAdd" icon="el-icon-plus">
              立即新增
            </el-button>
          </div>
        </div>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container" v-show="total > 0">
        <Pagination
            :total="total"
            :page.sync="formLabelAlign._page"
            :limit.sync="formLabelAlign._limit"
            @pagination="getStudentCodes"
        />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <dialogSlotVisible :centerDialogVisible="centerDialogVisible">
      <div class="dialog-title">{{ title }}</div>
      <el-form
          :model="formInline"
          ref="ruleForm"
          :rules="rules"
          :hide-required-asterisk="true"
          class="demo-form-inline"
          label-width="140px"
          size="small"
      >
        <el-form-item prop="number">
          <label slot="label">
            <span class="label-text">分校學生編號</span>
            <span class="star-mark"> *</span>
          </label>
          <el-input v-model="formInline.number" placeholder="請輸入分校學生編號"></el-input>
        </el-form-item>

        <el-form-item prop="phone">
          <label slot="label">
            <span class="label-text">電話號碼</span>
            <span class="star-mark"> *</span>
          </label>
          <el-input v-model.number="formInline.phone" placeholder="請輸入電話號碼"></el-input>
        </el-form-item>

        <el-form-item prop="chineseName">
          <label slot="label">
            <span class="label-text">分校學生ChineseName</span>
          </label>
          <el-input v-model="formInline.chineseName" placeholder="請輸入中文姓名"></el-input>
        </el-form-item>

        <el-form-item prop="engName">
          <label slot="label">
            <span class="label-text">分校學生EngName</span>
          </label>
          <el-input v-model="formInline.engName" placeholder="請輸入英文姓名"></el-input>
        </el-form-item>

        <el-form-item prop="given">
          <label slot="label">
            <span class="label-text">分校學生Given</span>
          </label>
          <el-input v-model="formInline.given" placeholder="請輸入Given名"></el-input>
        </el-form-item>

        <el-form-item prop="surname">
          <label slot="label">
            <span class="label-text">分校學生Surname</span>
          </label>
          <el-input v-model="formInline.surname" placeholder="請輸入Surname姓"></el-input>
        </el-form-item>

        <el-form-item prop="teacherName">
          <label slot="label">
            <span class="label-text">分校老師</span>
          </label>
          <el-input v-model="formInline.teacherName" placeholder="請輸入負責老師姓名"></el-input>
        </el-form-item>
      </el-form>

      <div class="page-button">
        <el-button type="warning" @click="onConfirm" icon="el-icon-check">確定</el-button>
        <el-button type="default" @click="onCancel" icon="el-icon-close">取消</el-button>
      </div>
    </dialogSlotVisible>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination";
import { payStatus } from "@/biz/static-data";
import { getStudentCodes, addStudentCodes, editStudentCodes } from "@/api/setting";
import dialogSlotVisible from "@/components/dialogSlotVisible";
import importExcel from "./components/importExcel";

export default {
  components: { dialogSlotVisible, Pagination, importExcel },
  filters: {
    studentCodeStatus(v) {
      return v === 0 ? "未選用" : "已認證";
    },
  },
  data() {
    return {
      itemId: 0,
      total: 0,
      title: '',
      payStatus: [],
      orderId: null,
      formInline: {
        number: '',
        phone: '',
        chineseName: '',
        teacherName: '',
        engName: '',
        given: '',
        surname: '',
      },
      dialogSlotVisible: false,
      centerDialogVisible: false,
      formLabelAlign: {
        numberLike: null,
        phoneLike: null,
        orderBy: null,
        _page: 1,
        _limit: 10,
      },
      tableData: [],
      rules: {
        number: [
          { required: true, message: '請輸入分校學生編號', trigger: 'blur' },
        ],
        phone: [
          { required: true, message: '請輸入電話號碼', trigger: 'blur' },
          { pattern: /^[0-9]+$/, message: '請輸入有效的數字電話號碼', trigger: 'blur' }
        ]
      }
    };
  },
  created() {
    this.payStatus = payStatus;
    this.getStudentCodes();
  },
  methods: {
    // Excel导入回调
    importExcel() {
      this.$message.success("Excel導入成功");
      this.getStudentCodes();
    },
    // 排序处理
    handleSortChange(column) {
      if (column.order === "descending") {
        this.formLabelAlign.orderBy = column.prop + " desc";
      } else if (column.order === "ascending") {
        this.formLabelAlign.orderBy = column.prop + " asc";
      } else {
        this.formLabelAlign.orderBy = null;
      }
      this.formLabelAlign._page = 1;
      this.getStudentCodes();
    },
    // 重置筛选
    onReset() {
      Object.keys(this.formLabelAlign).forEach((key) => {
        this.formLabelAlign[key] = null;
        if (key === "_page") {
          this.formLabelAlign[key] = 1;
        } else if (key === "_limit") {
          this.formLabelAlign[key] = 10; // 恢复默认10条/页
        }
      });
      this.getStudentCodes();
      this.$message.info("篩選條件已重置");
    },
    // 搜索
    onSearch() {
      this.formLabelAlign._page = 1; // 搜索时重置页码
      this.getStudentCodes();
    },
    // 取消对话框
    onCancel() {
      this.centerDialogVisible = false;
      // 重置表单验证
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields();
      }
    },
    // 确认提交
    onConfirm() {
      this.$refs.ruleForm.validate((valid) => {
        if (valid) {
          // 加载中提示
          const loading = this.$loading({
            lock: true,
            text: '提交中...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          });

          if (this.itemId != null) {
            // 编辑
            this.editStudentCodes()
                .then(() => {
                  this.$message.success("編輯成功");
                })
                .catch(err => {
                  console.error('编辑失败：', err);
                })
                .finally(() => {
                  loading.close();
                });
          } else {
            // 新增
            this.addStudentCodes()
                .then(() => {
                  this.$message.success("新增成功");
                })
                .catch(err => {
                  console.error('新增失败：', err);
                })
                .finally(() => {
                  loading.close();
                });
          }
        } else {
          return false;
        }
      });
    },
    // 编辑
    onEdit(row, id) {
      this.title = '編輯';
      this.formInline.number = row.number;
      this.formInline.phone = row.phone;
      this.formInline.chineseName = row.chineseName;
      this.formInline.teacherName = row.teacherName;
      this.formInline.engName = row.engName;
      this.formInline.given = row.given;
      this.formInline.surname = row.surname;
      this.itemId = id;
      this.centerDialogVisible = true;
    },
    // 新增
    onAdd() {
      this.title = '新增';
      // 清空表单
      this.formInline = {
        number: '',
        phone: '',
        chineseName: '',
        teacherName: '',
        engName: '',
        given: '',
        surname: '',
      };
      this.itemId = null;
      this.centerDialogVisible = true;
    },
    // 编辑学生编号
    editStudentCodes() {
      let from = {
        number: this.formInline.number,
        phone: this.formInline.phone,
        chineseName: this.formInline.chineseName,
        teacherName: this.formInline.teacherName,
        engName: this.formInline.engName,
        given: this.formInline.given,
        surname: this.formInline.surname,
      };
      return editStudentCodes(this.itemId, from)
          .then(() => {
            this.getStudentCodes();
          })
          .finally(() => {
            this.centerDialogVisible = false;
          });
    },
    // 新增学生编号
    addStudentCodes() {
      let from = {
        number: this.formInline.number,
        phone: this.formInline.phone,
        chineseName: this.formInline.chineseName,
        teacherName: this.formInline.teacherName,
        engName: this.formInline.engName,
        given: this.formInline.given,
        surname: this.formInline.surname,
      };
      return addStudentCodes(from)
          .then(() => {
            this.getStudentCodes();
          })
          .finally(() => {
            this.centerDialogVisible = false;
          });
    },
    // 获取学生编号列表
    getStudentCodes() {
      let formLabelAlign = {
        phoneLike: this.formLabelAlign.phoneLike,
        numberLike: this.formLabelAlign.numberLike,
        orderBy: this.formLabelAlign.orderBy,
        _page: this.formLabelAlign._page - 1,
        _limit: this.formLabelAlign._limit,
      };

      // 加载中提示
      const loading = this.$loading({
        lock: true,
        text: '加載數據中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      getStudentCodes(formLabelAlign)
          .then((v) => {
            this.tableData = v.data || [];
            this.total = Number(v.headers["x-total-count"]) || 0;
          })
          .catch(err => {
            this.$message.error("獲取學生編號列表失敗，請稍後重試");
            console.error('获取数据失败：', err);
            this.tableData = [];
            this.total = 0;
          })
          .finally(() => {
            loading.close();
          });
    },
  },
};
</script>

<style lang="scss" scoped>
/* 基础样式 */
.page {
  padding: 16px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

/* 统一风格页头 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7ed;

  .header-left {
    .page-title {
      font-size: 22px;
      font-weight: 600;
      color: #B62D2E;
      margin: 0 0 4px 0;
    }
    .page-subtitle {
      font-size: 14px;
      color: #6c757d;
      margin: 0;
    }
  }

  .header-right {
    .header-actions {
      display: flex;
      gap: 12px;
      align-items: center;

      .import-btn {
        margin-right: 8px;
      }
    }
  }
}

/* 布局容器 */
.layout {
  background: #fff;
  border-radius: 8px;
  //padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 搜索卡片样式 */
.search-card {
  margin-bottom: 20px;

  /* PC端搜索样式 */
  .pc-search {
    .search-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr) 100px 100px;
      gap: 12px;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 6px;

      .search-item {
        .search-text {
          margin-bottom: 4px;
          font-size: 12px;
          color: #6c757d;
          font-weight: 500;
        }
      }

      .search-button, .reset-button {
        display: flex;
        align-items: flex-end;

        .el-button {
          width: 100%;
        }
      }
    }
  }

  /* 移动端搜索样式 */
  .mobile-search {
    display: none;

    .mobile-search-form {
      padding: 16px 8px;
    }

    .mobile-search-buttons {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
  }
}

/* 数据容器样式 */
.data-container {
  width: 100%;
  margin-bottom: 20px;

  /* PC端表格样式 */
  .pc-table {
    ::v-deep {
      .el-table {
        --el-table-header-text-color: #4e5969;
        --el-table-row-hover-bg-color: #f8f9fa;
      }

      .el-table th {
        background: #f8f9fa;
        font-weight: 600;
      }

      .student-number {
        font-weight: 600;
        color: #166ea5;
      }

      .name-wrapper {
        .chinese-name {
          font-weight: 500;
          margin-bottom: 2px;
        }
        .english-name {
          font-size: 12px;
          color: #6c757d;
        }
      }

      .edit-btn {
        color: #409eff;
      }
    }
  }

  /* 手机端卡片样式 */
  .mobile-cards {
    display: none;
    flex-direction: column;
    gap: 16px;

    /* 学生卡片 */
    .student-card {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      border: 1px solid #e9ecef;
      transition: all 0.2s ease;

      &:hover {
        border-color: #166ea5;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      /* 卡片头部 */
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #dee2e6;

        .student-number {
          display: flex;
          align-items: center;
          gap: 6px;

          .label {
            font-size: 12px;
            color: #6c757d;
          }

          .value {
            font-weight: 600;
            color: #166ea5;
          }
        }
      }

      /* 卡片主体 */
      .card-body {
        margin-bottom: 16px;

        .info-item {
          display: flex;
          margin-bottom: 8px;
          line-height: 1.4;

          .label {
            font-size: 12px;
            color: #6c757d;
            min-width: 60px;
            flex-shrink: 0;
          }

          .value {
            font-size: 14px;
            color: #1f2937;
            flex: 1;

            &.name-value {
              display: flex;
              flex-direction: column;

              .english-name {
                font-size: 12px;
                color: #6c757d;
                margin-top: 2px;
              }
            }
          }
        }
      }

      /* 卡片操作栏 */
      .card-actions {
        padding-top: 12px;
        border-top: 1px solid #f1f3f5;
      }
    }

    /* 空数据提示 */
    .empty-tip {
      text-align: center;
      padding: 40px 20px;
      color: #999;

      i {
        font-size: 48px;
        margin-bottom: 16px;
        display: block;
        color: #ced4da;
      }

      p {
        font-size: 16px;
        margin: 0 0 20px 0;
      }
    }
  }
}

/* 分页容器 */
.pagination-container {
  text-align: right;
  padding: 10px 0;
}

/* 对话框样式 */
::v-deep {
  .dialog-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e4e7ed;
    color: #1f2937;
  }

  .label-text {
    font-size: 14px;
    color: #4e5969;
  }

  .star-mark {
    color: #f56c6c;
    margin-left: 4px;
  }

  .page-button {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e4e7ed;

    .el-button {
      min-width: 80px;
    }
  }
}

/* 响应式适配 */
@media (max-width: 768px) {
  .page {
    padding: 8px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-bottom: 0px;
  }

  .layout {
    padding: 16px;
  }

  /* 隐藏PC搜索，显示移动端搜索 */
  .search-card .pc-search {
    display: none;
  }
  .search-card .mobile-search {
    display: block;
  }

  /* 隐藏PC表格，显示手机卡片 */
  .data-container .pc-table {
    display: none;
  }
  .data-container .mobile-cards {
    display: flex;
  }

  /* 分页适配 */
  .pagination-container {
    text-align: center;
  }
}

/* 适配平板屏幕 */
@media (min-width: 769px) and (max-width: 1024px) {
  .layout {
    padding: 20px;
  }

  .search-card .search-row {
    grid-template-columns: repeat(2, 1fr) 100px 100px;
  }
}
</style>